export interface LegalSection {
  heading: string
  body: string[]
  /** Rendered as fine print: legally complete, visually quiet. */
  fine?: boolean
}

export interface LegalDoc {
  slug: string
  title: string
  eyebrow: string
  updated: string
  intro: string
  sections: LegalSection[]
}

const UPDATED = 'September 2026'
const SUPPORT = 'support@gritvue.com'

export const legalDocs: LegalDoc[] = [
  {
    slug: 'shipping',
    title: 'Shipping policy',
    eyebrow: 'Fast, tracked, and out of Texas',
    updated: UPDATED,
    intro:
      'Every GritVue order ships from our warehouse in Texas. No overseas transit, no customs paperwork, no guessing where your gear is.',
    sections: [
      {
        heading: 'Carrier',
        body: [
          'Every order ships USPS from our Fort Worth, Texas warehouse at 4820 Wingate Industrial Blvd, Building C, Dock 4, Fort Worth, TX 76106.',
          'All packages are insured and tracked end to end, on single units and multi-packs alike.',
        ],
      },
      {
        heading: 'Processing time',
        body: [
          'Orders placed before 2:00pm CT on a business day ship the same day. Orders placed after that, or on a weekend or holiday, ship the next business day.',
          'You receive a USPS tracking number by email the moment your label is created.',
        ],
      },
      {
        heading: 'Delivery time',
        body: [
          'Standard U.S. delivery is 3 to 6 business days after dispatch. Alaska, Hawaii and U.S. territories typically take 5 to 9 business days.',
          'Expedited options, when available, are shown at checkout with their own delivery estimate.',
        ],
      },
      {
        heading: 'Shipping cost',
        body: [
          'Free standard U.S. shipping on every multi-pack: the Full-Shift Kit, the Max Value pack and the Business / Crew Pack.',
          'Single X1 orders have shipping calculated at checkout before you pay. There are no handling fees, surcharges or hidden add-ons.',
        ],
      },
      {
        heading: 'Lost, delayed or damaged in transit',
        body: [
          'If tracking stops moving for more than 5 business days, email us and we will open a carrier claim and ship a replacement. You do not wait on the claim.',
          'If a package arrives damaged, email us and we ship a replacement the same business day.',
        ],
      },
      {
        heading: 'Address changes',
        body: [
          'Email us within 2 hours of ordering and we will update the address before the label is scanned. After dispatch we will work with the carrier to reroute at no cost to you.',
        ],
      },
      {
        heading: 'International',
        body: [
          'We currently ship within the United States. If you are outside the U.S., email us and we will let you know the moment your country is live.',
        ],
      },
    ],
  },
  {
    slug: 'returns',
    title: 'Returns & refunds',
    eyebrow: '30 days, return shipping on us',
    updated: UPDATED,
    intro:
      'Try the X1 on a real job. If it is not for you, send it back within 30 days of delivery for a full refund. We pay the return shipping.',
    sections: [
      {
        heading: 'The short version',
        body: [
          '30 days from delivery. Full refund. Free return label. No restocking fee. You do not need to explain yourself.',
          'Used it on the job already? That is fine. That is the point of the trial.',
        ],
      },
      {
        heading: 'How to start a return',
        body: [
          `Email ${SUPPORT} with your order number and the word "return". We reply with a prepaid label, usually within one business day.`,
          'Pack the frame and lenses in any box you have. The original packaging is not required.',
        ],
      },
      {
        heading: 'When you get your money',
        body: [
          'Refunds are issued to your original payment method within 2 business days of the return being scanned by the carrier, not after it reaches us.',
          'Your bank typically posts the refund 3 to 5 business days after that.',
        ],
      },
      {
        heading: 'Multi-packs',
        body: [
          'Returning a multi-pack? Send back what you are not keeping. We refund the difference at the per-unit price of the pack you keep, so you are never charged the single-unit rate as a penalty.',
        ],
      },
      {
        heading: 'Exchanges',
        body: [
          'Want a different pack size instead of a refund? Say so in your email and we ship the new pack as soon as the return is scanned. No second shipping charge.',
        ],
      },
      {
        heading: 'What is not covered here',
        body: [
          'Breakage, cracks and scratched lenses are not returns. They are covered under the GritVue Lifetime Free Replacement. See that page.',
        ],
      },
    ],
  },
  {
    slug: 'guarantee',
    title: 'Lifetime free replacement',
    eyebrow: 'Break it and we replace it. Free. Forever.',
    updated: UPDATED,
    intro:
      'Work gear gets destroyed. That is not your fault and it is not your problem. Every GritVue X1 is covered for free replacement for as long as you own it.',
    sections: [
      {
        heading: 'What is covered',
        body: [
          'A cracked or snapped frame. A broken hinge or temple. A light module that stops working. A port that stops charging. A battery that will not hold a charge. A scratched or cracked Clear, Amber or Smoke lens.',
          'Damage from real work is covered: dropped off a ladder, run over in the shop, crushed in a toolbox, left on a truck bumper. We do not argue about how it happened.',
        ],
      },
      {
        heading: 'How long it lasts',
        body: [
          'Every X1 is covered for as long as you own it, with no expiration date. That applies to single units, the Full-Shift Kit, the Max Value pack and the Business / Crew Pack alike.',
          'Either way there is no registration to remember and no receipt hunt. Your order is already in our system.',
        ],
      },
      {
        heading: 'How to claim it',
        body: [
          `Email ${SUPPORT} with your order number and one line about what happened. No forms and no receipt hunt. Your order is already in our system, and we take it from there.`,
          'Most claims are handled the same day they come in.',
        ],
      },
      {
        heading: 'How many times',
        body: [
          'As many times as work breaks it. One replacement per damaged unit, and there is no lifetime cap on legitimate claims. You buy the X1 once.',
        ],
      },
      {
        heading: 'Fine print',
        fine: true,
        body: [
          'Coverage applies to the original purchaser only and is not transferable if the product is resold or given away.',
          'Lifetime coverage applies to every unit purchased, single or multi-pack. Max Value and Business / Crew Pack orders additionally receive priority claim handling.',
          'The replacement unit itself is free. A flat $6.95 shipping and handling fee per replacement applies and is paid by the customer at the time of the claim. This fee covers fulfillment only and is not a charge for the product.',
          'Every claim requires proof of damage in the form of a photo of the damaged unit. Replacements may be held until that proof is received.',
          'In cases where the claim history or circumstances warrant additional verification, GritVue may also require the damaged unit to be returned to our facility before a replacement is issued. In those cases return shipping is arranged and paid by the customer. Routine claims are resolved from the photo alone.',
          'Lost, misplaced or stolen units are not covered. This program covers damage and functional failure of a unit you still have, not replacement of units that are no longer in your possession. A police report does not change this.',
          'Replacements are issued per damaged unit, up to the number of units on the original order. Claims for more units than were purchased will be declined.',
          'The program covers use as work eyewear. Intentional destruction, disassembly, unauthorized modification of the electronics, and damage caused by using the product outside its intended purpose are not covered.',
          'We may decline or pause claims where the pattern indicates resale, abuse of the program or fraudulent submissions.',
          'Replacements are provided as the same model where available, or the closest current equivalent if the model has changed. No cash value, and the replacement does not restart or extend the 30-day return window.',
          'GritVue reserves the right to modify this program for future purchases. Changes never reduce coverage on orders already placed.',
        ],
      },
    ],
  },
  {
    slug: 'privacy',
    title: 'Privacy policy',
    eyebrow: 'Your data is not the product',
    updated: UPDATED,
    intro:
      'We collect the minimum needed to ship your order, support it and honor your lifetime replacement. We do not sell your personal information.',
    sections: [
      {
        heading: 'What we collect',
        body: [
          'Order information: name, shipping address, email, phone number and the items you bought.',
          'Payment information is entered on our payment processor and is never stored on our servers. We see only the last four digits and the card type.',
          'Usage information: pages viewed and general device and location data, through cookies and advertising pixels.',
        ],
      },
      {
        heading: 'Why we collect it',
        body: [
          'To process, ship and support your order, and to verify lifetime replacement claims.',
          'To send order and delivery updates, and marketing messages if you opted in.',
          'To measure and improve our advertising so the right tradespeople see us.',
        ],
      },
      {
        heading: 'Who we share it with',
        body: [
          'Service providers that make the business run: our ecommerce platform, payment processor, shipping carriers, email and SMS provider, and advertising platforms such as Meta and TikTok.',
          'We never sell your personal information, and we never share your email or phone number with unrelated brands or data brokers.',
        ],
      },
      {
        heading: 'Your choices',
        body: [
          'Unsubscribe from email at the bottom of any message. Reply STOP to any text to end SMS.',
          `Ask us to access, correct or delete your data by emailing ${SUPPORT}. We respond within 30 days.`,
          'You can opt out of advertising cookies through your browser settings or your device ad-tracking controls.',
        ],
      },
      {
        heading: 'Security and retention',
        body: [
          'Data is transmitted over encrypted connections and kept only as long as needed for orders, warranty claims and legal or tax requirements.',
          'We do not knowingly collect information from anyone under 16.',
        ],
      },
    ],
  },
  {
    slug: 'terms',
    title: 'Terms of service',
    eyebrow: 'The rules, written to be read',
    updated: UPDATED,
    intro:
      'By ordering from GritVue you agree to these terms. We have kept them short and in plain English on purpose.',
    sections: [
      {
        heading: 'Orders and pricing',
        body: [
          'All prices are in U.S. dollars. Taxes are calculated at checkout where required.',
          'We may correct obvious pricing or typographical errors. If a correction affects your order, we contact you first and you can cancel for a full refund.',
          'Promotional codes may not be combined with each other unless the checkout allows it, and are limited to one use per customer.',
        ],
      },
      {
        heading: 'What the X1 is',
        body: [
          'The GritVue X1 is illuminated work eyewear: a work glasses frame with built-in hands-free lighting and three interchangeable lenses.',
          'Use common sense on the job and follow your employer or site requirements for any specific protective equipment your work calls for.',
        ],
      },
      {
        heading: 'Prescription lenses',
        body: [
          'The X1 uses removable lenses, and opticians can fit prescription lenses into the clear lens position. Lens work performed by a third party does not void your lifetime replacement on the frame or electronics.',
        ],
      },
      {
        heading: 'Prescription fine print',
        fine: true,
        body: [
          'Any prescription lens work is performed by an optician of your choosing, who is an independent third party. GritVue does not perform, supervise, price or warrant that work, and is not responsible for an optician declining the job, for the fit, quality, tint, coating or optical accuracy of lenses they produce, for damage caused during fitting, or for any cost you incur with them.',
          'Lens curvature and frame geometry mean certain prescriptions, including high corrections and strong astigmatism, may not be suitable. Confirm suitability with your optician before ordering. An optician declining a prescription is not a product defect and is not covered by the Lifetime Free Replacement, though the 30-day return policy still applies.',
          'Prescription lenses fitted by a third party are not covered by the Lifetime Free Replacement. Coverage continues to apply to the frame, electronics and the original Clear, Amber and Smoke lenses supplied by GritVue.',
        ],
      },
      {
        heading: 'Returns and replacement',
        body: [
          'Our 30-day return policy and Lifetime Free Replacement are part of these terms. See those pages for how each one works.',
        ],
      },
      {
        heading: 'Intellectual property',
        body: [
          'GritVue, GritVue X1 and our product photography, copy and branding are ours. Please do not copy them for resale or paid advertising.',
        ],
      },
      {
        heading: 'Liability',
        body: [
          'Our liability for any claim related to a product is limited to the amount you paid for it, which is the standard limit for consumer goods of this kind. Nothing here removes rights you have under applicable consumer law.',
        ],
      },
      {
        heading: 'Questions and disputes',
        body: [
          `Email ${SUPPORT} first. We resolve nearly everything the same week. These terms are governed by the laws of the State of Texas.`,
        ],
      },
    ],
  },
  {
    slug: 'contact',
    title: 'Contact us',
    eyebrow: 'A real person answers',
    updated: UPDATED,
    intro:
      'Order help, replacement claims, crew pricing or a question before you buy. Write to us and you get a human, not a ticket bot.',
    sections: [
      {
        heading: 'Email',
        body: [`${SUPPORT}. Answered Monday to Friday, 8:00am to 6:00pm CT, usually within one business day.`],
      },
      {
        heading: 'Replacement claims',
        body: [
          'Send your order number and one line about what broke. No forms to fill out. We take it from there and most claims are handled the same day.',
        ],
      },
      {
        heading: 'Crews, shops and fleets',
        body: [
          'Outfitting more than 30 people, or need an invoice and net terms? Email us with your company name and headcount and we will quote it directly.',
        ],
      },
      {
        heading: 'Where we are',
        body: [
          'GritVue Fulfillment, 4820 Wingate Industrial Blvd, Building C, Dock 4, Fort Worth, TX 76106, United States.',
          'Every order ships USPS from this warehouse, insured and tracked. Returns come back to the same address on the prepaid label we send you.',
        ],
      },
    ],
  },
]

export function getLegalDoc(slug: string): LegalDoc | undefined {
  return legalDocs.find((doc) => doc.slug === slug)
}
